import { useState } from "react";
import { DotsThree } from "phosphor-react";
import { VenueReview } from "common/types";
import { bucketUrlPrefix } from "common/config";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import ImageListViewer from "@/components/ImageListViewer/ImageListViewer";
import ScrollContainer from "react-indiana-drag-scroll";
import AnalyticsService from "@/services/analytics";
import { createPortal } from "react-dom";
import FullImageViewer from "@/components/FullImageViewer";

export interface ExperienceCardProps {
  review: VenueReview;
  hasMenu?: boolean;
  onMenuClick?: () => void;
}

const ExperienceCard = ({
  review,
  hasMenu = true,
  onMenuClick,
}: ExperienceCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const [showImageList, setShowImageList] = useState<{
    show: boolean;
    current: number;
  }>({
    show: false,
    current: 0,
  });

  const handleOpenFlagTracking = () => {
    AnalyticsService.trackGAEventHandler("flagReviewReportOpen");
    AnalyticsService.trackMx("flagReviewReportOpen", {});
  };

  const handleOpenFullReviewImage = (photo) => {
    setCurrentReviewImage(photo);
    setFullReviewImageView(true);

    // Track opening the full image viewer
    AnalyticsService.trackGAEventHandler("openPhotoLightbox");
    AnalyticsService.trackMx("openPhotoLightbox", {
      VenueId: review.businessId,
      VenueBrandId: "Review venue brand id",
      VenueBrandName: "Review venue brand name",
      VenueAddress: "Review venue address",
      VenueName: "Review venue name",
      VenueTypes: "Review venue types",
      CategoryName: "Review photo",
      SubcategoryName: "Review photo",
    });
  };

  const [fullReviewImageView, setFullReviewImageView] = useState(false);
  const [currentReviewImage, setCurrentReviewImage] = useState<any>(null);

  return (
    <div className="flex flex-col items-start w-full">
      <article className="flex flex-col items-start w-full">
        {/* User Info Row */}
        <div className="flex gap-2 items-center px-4 py-3 w-full">
          {/* Avatar */}
          <div className="relative flex items-center shrink-0">
            {review.userProfilePhoto ? (
              <img
                className="w-9 h-9 rounded-full shadow-md object-cover"
                src={bucketUrlPrefix + review.userProfilePhoto}
                alt={review.userFirstName}
              />
            ) : (
              <NameInitialsAvatar
                name={review.userFirstName?.split("")[0]}
                bgColor={
                  review?.userProfileColorCode
                    ? review.userProfileColorCode
                    : "#8751FF"
                }
                textColor="white"
                textSize="20px"
                size="36px"
                borderColor={
                  review?.userProfileColorCode
                    ? review.userProfileColorCode
                    : "#8751FF"
                }
              />
            )}
          </div>

          {/* User Details */}
          <div className="flex flex-1 flex-col items-start leading-none min-w-0">
            <h3 className="label-m-medium text-fg-neutral-strong truncate w-full">
              {review.userFirstName} {review.userLastName}
            </h3>
            <p className="label-s-regular text-fg-neutral-subtle overflow-hidden text-ellipsis whitespace-nowrap w-full">
              {review.timeAgo} | Shared a <span className="font-bold">&quot;{review.accessibility_experience === "cool" ? "Sweet" : "Sour"}&quot;</span> experience
            </p>
          </div>

          {/* Actions */}
          {hasMenu && (
            <div className="flex gap-2 items-center justify-center shrink-0">
              <button
                type="button"
                onClick={onMenuClick}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                aria-label="More options"
              >
                <DotsThree size={24} className="text-fg-neutral-strong" weight="bold" />
              </button>
            </div>
          )}
        </div>

        {/* Tags and Content */}
        {(review.userAbility.length > 0 || review.notes || review.reviewPhotos?.length > 0) && (
          <div className="flex flex-col gap-4 w-full px-4 md:px-8 pb-6">
            {/* Tags */}
            {review.userAbility.length > 0 && (
              <div className="flex flex-wrap gap-2 items-center w-full">
                {review.userAbility.split(",").map((tag, index) => (
                  <div
                    key={index}
                    className="bg-bg-brand-secondary-default border border-border-token-brand-minimal flex items-center justify-center min-h-10 px-3 py-2 rounded-full hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active transition-colors focus-blue"
                  >
                    <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                      {tag.trim()}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Review Text */}
            {review.notes && (
              <div className="flex flex-col items-start justify-center min-h-6 w-full">
                <p className="label-m-medium text-fg-neutral-strong">
                  {isExpanded ? review.notes : `${review.notes.slice(0, 150)}`}
                  {review.notes.length > 150 && (
                    <>
                      {!isExpanded && " ..."}
                      <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="ml-1 label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline focus-blue"
                        aria-label={isExpanded ? "Show less" : "Show more"}
                      >
                        {isExpanded ? "less" : "more"}
                      </button>
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Photos */}
            {review.reviewPhotos.length > 0 && (
              <div className="flex gap-2 items-start">
                <ScrollContainer className="flex flex-row space-x-2">
                  {review.reviewPhotos.map((photo, index) => (
                    <div
                      key={index}
                      className="relative rounded-lg shrink-0 w-16 h-16"
                    >
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          handleOpenFullReviewImage(photo);
                        }}
                        className="cursor-pointer flex-shrink-0 focus-blue rounded-lg"
                        aria-label={photo.altText ? `View photo: ${photo.altText}` : `View review photo ${index + 1}`}
                      >
                        <img
                          src={bucketUrlPrefix + photo.path}
                          className="absolute inset-0 w-full h-full object-cover rounded-lg"
                          alt={photo.altText || "Review photo"}
                        />
                      </button>
                    </div>
                  ))}
                </ScrollContainer>

                {/* Full image view */}
                {fullReviewImageView &&
                  currentReviewImage &&
                  createPortal(
                    <div className="fixed inset-0 z-[999] bg-black bg-opacity-90">
                      <FullImageViewer
                        images={review.reviewPhotos.map((photo, index) => ({
                          src: bucketUrlPrefix + photo.path,
                          alt: photo.altText || "Review photo" + (index + 1),
                          tag_name: "Review Photo",
                          category_name: photo.tagApplied,
                          created_at: null,
                          photo_type: "Review type",
                          review_photos_id: null,
                          width: photo.width,
                          height: photo.height,
                          sort_order: photo.sort_order,
                          qtags: [],
                          tags: [
                            {
                              tagName: "Review Photo",
                              categoryDisplaySectionId: "",
                            },
                          ],
                          displaySections: {},
                          venueId: review.businessId,
                          venueName: "Review venue name",
                          venueAddress: "Review venue address",
                          venueBrandId: "Review venue brand id",
                          venueBrandName: "Review venue brand name",
                          venueTypes: "Review venue types",
                        }))}
                        currentIndex={
                          review.reviewPhotos.findIndex((p) => p === currentReviewImage) || 0
                        }
                        onClose={() => setFullReviewImageView(false)}
                        onFlag={() => { }}
                        showFlag={false}
                        showRightTab={false}
                      />
                    </div>,
                    document.body,
                  )}
              </div>
            )}

            <ImageListViewer
              title="Photos of Reviews"
              currentImage={showImageList.current}
              images={review.reviewPhotos.map((photo) => ({
                id: photo.path,
                url: bucketUrlPrefix + photo.path,
                username: review.user.name,
                timeAgo: review.timeAgo,
                note: photo.altText,
              }))}
              visible={showImageList.show}
              onClose={() => {
                setShowImageList({
                  show: false,
                  current: 0,
                });
              }}
            />
          </div>
        )}
      </article>
    </div>
  );
};

export default ExperienceCard;
